import React, { useState, useEffect } from 'react';
import { 
  Trophy, Medal, Award, Users, GraduationCap, 
  Crown, Star, Sparkles, ArrowLeft, School
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Link } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const Leaderboard = () => {
  const [loading, setLoading] = useState(true);
  const [studentsLeaderboard, setStudentsLeaderboard] = useState([]);
  const [schoolsLeaderboard, setSchoolsLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('students');

  useEffect(() => {
    fetchLeaderboards();
  }, []);

  const fetchLeaderboards = async () => {
    setLoading(true);
    try {
      const response = await api.get('/leaderboard');
      setStudentsLeaderboard(response.data.students || []);
      setSchoolsLeaderboard(response.data.schools || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger le classement',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="text-pink-300 font-bold text-lg">#{rank}</span>;
  };

  const getRankBgColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/30 to-amber-500/30 border-yellow-400/50';
    if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400/50';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-600/50';
    return 'bg-white/5 border-white/10';
  };

  const getLevelStage = (level) => {
    if (level >= 11) return { emoji: '✨', name: 'Transcendance' };
    if (level >= 9) return { emoji: '🌻', name: 'Épanouissement' };
    if (level >= 7) return { emoji: '🌺', name: 'Floraison' };
    if (level >= 5) return { emoji: '🌸', name: 'Bourgeon' };
    if (level >= 3) return { emoji: '🌿', name: 'Pousse' };
    return { emoji: '🌱', name: 'Graine' };
  };

  const StudentCard = ({ student, rank }) => {
    const stage = getLevelStage(student.level);
    
    return (
      <Card 
        className={`p-4 ${getRankBgColor(rank)} border transition-all hover:scale-[1.02]`}
        data-testid={`student-rank-${rank}`}
      >
        <div className="flex items-center gap-4">
          {/* Rank */}
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/10">
            {getRankIcon(rank)}
          </div>
          
          {/* Avatar & Name */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-white font-semibold">{student.name}</span>
              <span className="text-lg">{stage.emoji}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-pink-300">{stage.name}</span>
              {student.school && (
                <span className="text-pink-400 text-xs">• {student.school}</span>
              )}
            </div>
          </div>
          
          {/* Stats */}
          <div className="text-right">
            <div className="text-yellow-400 font-bold text-lg">{student.points.toLocaleString()}</div>
            <div className="text-pink-300 text-xs">points</div>
            <div className="text-cyan-400 text-sm font-medium">Niv. {student.level}</div>
          </div>
        </div>
      </Card>
    );
  };

  const SchoolCard = ({ school, rank }) => {
    return (
      <Card 
        className={`p-4 ${getRankBgColor(rank)} border transition-all hover:scale-[1.02]`}
        data-testid={`school-rank-${rank}`}
      >
        <div className="flex items-center gap-4">
          {/* Rank */}
          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/10">
            {getRankIcon(rank)}
          </div>
          
          {/* School Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <School className="w-4 h-4 text-pink-300" />
              <span className="text-white font-semibold">{school.name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-pink-300">
              <Users className="w-3 h-3" />
              <span>{school.studentCount} élèves actifs</span>
            </div>
          </div>
          
          {/* Stats */}
          <div className="text-right">
            <div className="text-yellow-400 font-bold text-lg">{school.totalPoints.toLocaleString()}</div>
            <div className="text-pink-300 text-xs">points totaux</div>
            <div className="text-cyan-400 text-sm">
              Moy: {Math.round(school.totalPoints / (school.studentCount || 1))} pts
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-pink-200 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </Link>
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-400" />
            <span className="text-xl font-bold text-white">Classement</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Hero Section */}
        <Card className="p-6 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 backdrop-blur-md border-yellow-400/30 mb-6">
          <div className="text-center">
            <div className="flex justify-center gap-2 mb-4">
              <Star className="w-8 h-8 text-yellow-400" />
              <Trophy className="w-10 h-10 text-yellow-300" />
              <Star className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Tableau d'Honneur</h1>
            <p className="text-yellow-100">
              Les meilleurs élèves et établissements de notre communauté!
            </p>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-white/10">
            <TabsTrigger 
              value="students" 
              className="data-[state=active]:bg-pink-500/50"
              data-testid="tab-students"
            >
              <GraduationCap className="w-4 h-4 mr-2" />
              Top 20 Élèves
            </TabsTrigger>
            <TabsTrigger 
              value="schools"
              className="data-[state=active]:bg-pink-500/50"
              data-testid="tab-schools"
            >
              <School className="w-4 h-4 mr-2" />
              Top 20 Établissements
            </TabsTrigger>
          </TabsList>

          {/* Students Leaderboard */}
          <TabsContent value="students">
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Card key={i} className="p-4 bg-white/5 border-white/10 animate-pulse">
                    <div className="h-12 bg-white/10 rounded" />
                  </Card>
                ))}
              </div>
            ) : studentsLeaderboard.length > 0 ? (
              <div className="space-y-3">
                {studentsLeaderboard.map((student, index) => (
                  <StudentCard key={student.id} student={student} rank={index + 1} />
                ))}
              </div>
            ) : (
              <Card className="p-8 bg-white/10 border-white/20 text-center">
                <GraduationCap className="w-12 h-12 text-pink-300 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">Pas encore de classement</h3>
                <p className="text-pink-200 text-sm">
                  Complétez des leçons et des quiz pour apparaître dans le classement!
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Schools Leaderboard */}
          <TabsContent value="schools">
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Card key={i} className="p-4 bg-white/5 border-white/10 animate-pulse">
                    <div className="h-12 bg-white/10 rounded" />
                  </Card>
                ))}
              </div>
            ) : schoolsLeaderboard.length > 0 ? (
              <div className="space-y-3">
                {schoolsLeaderboard.map((school, index) => (
                  <SchoolCard key={school.name} school={school} rank={index + 1} />
                ))}
              </div>
            ) : (
              <Card className="p-8 bg-white/10 border-white/20 text-center">
                <School className="w-12 h-12 text-pink-300 mx-auto mb-4" />
                <h3 className="text-white font-semibold mb-2">Pas encore d'établissements</h3>
                <p className="text-pink-200 text-sm">
                  Les établissements scolaires apparaîtront ici lorsque leurs élèves 
                  participeront et gagneront des points!
                </p>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Info Card */}
        <Card className="p-4 bg-purple-500/10 border-purple-400/30 mt-6">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-300 mt-0.5" />
            <div>
              <h4 className="text-white font-medium mb-1">Comment gagner des points?</h4>
              <ul className="text-purple-200 text-sm space-y-1">
                <li>• <span className="text-yellow-400">+50 pts</span> par leçon terminée</li>
                <li>• <span className="text-yellow-400">+100 pts</span> par quiz réussi</li>
                <li>• <span className="text-yellow-400">+10 pts</span> par check-in quotidien</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      <BottomNav />
    </div>
  );
};

export default Leaderboard;
